import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { AttendanceServicesActions } from './attendance-services.actions';
import { Injectable } from '@angular/core';
import { patch, updateItem } from '@ngxs/store/operators';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { AttendanceServicesService } from '../services/attendance-services.service';
import {
  InvoicingServiceData,
  ServiceDayData,
} from '../interfaces/invoicingService-data.model';
import { InvoicingServiceFormFilter } from '../interfaces/invoicingService-form-filter.model';
import {
  InvoicingServiceItemResponse,
  InvoicingServicesResponse,
} from '../interfaces/invoicingService-response.model';
import { InvoicingServicesForm } from '../interfaces/invoicingServices-form.model';

export class AttendanceServicesStateModel {
  public invoicingServicesItems?: Array<InvoicingServiceData>;
  invoicingServicesItemsResponse: Array<InvoicingServiceItemResponse>;
  invoicingServicesResponse: InvoicingServicesResponse;
  invoicingServiceForm: {
    model: InvoicingServiceData;
  };
  filters: {
    model: InvoicingServiceFormFilter;
  };
  public isListSmaller: boolean;
  public isLoading: boolean;
  public error: string;
}

const defaults = {
  invoicingServicesItems: null,
  invoicingServicesItemsResponse: null,
  invoicingServicesResponse: null,
  invoicingServiceForm: {
    model: null,
  },
  filters: {
    model: null,
  },
  isListSmaller: true,
  isLoading: false,
  error: null,
};

@State<AttendanceServicesStateModel>({
  name: 'attendanceServices',
  defaults,
})
@Injectable()
export class AttendanceServicesState {
  constructor(
    private attendanceServicesService: AttendanceServicesService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(AttendanceServicesActions.GetAttendanceServices)
  getAttendanceServices(
    ctx: StateContext<AttendanceServicesStateModel>,
    { year, month, schoolId, exerciseId }: AttendanceServicesActions.GetAttendanceServices
  ) {
    ctx.patchState({ isLoading: true });

    return this.attendanceServicesService
      .getAttendanceServicesByFilter(year, month, schoolId, exerciseId)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new AttendanceServicesActions.GetAttendanceServicesSuccess(response)
          ),
        error: (error) =>
          ctx.dispatch(new AttendanceServicesActions.Error(error)),
      });
  }

  @Action(AttendanceServicesActions.GetAttendanceServicesSuccess)
  getAttendanceServicesSuccess(
    ctx: StateContext<AttendanceServicesStateModel>,
    {
      invoicingServices,
    }: AttendanceServicesActions.GetAttendanceServicesSuccess
  ) {
    ctx.patchState({
      invoicingServicesItems: invoicingServices,
      isLoading: false,
    });
  }

  @Action(AttendanceServicesActions.PreEditAttendanceServices)
  preEditAttendanceServices(
    ctx: StateContext<AttendanceServicesStateModel>,
    {
      invoicingServicesForm,
    }: AttendanceServicesActions.PreEditAttendanceServices
  ) {
    let items = this.getAttendanceServicesItemsResponse(invoicingServicesForm);
    ctx.patchState({
      invoicingServicesItemsResponse: items,
    });
  }

  @Action(AttendanceServicesActions.EditAttendanceServiceRow)
  editAttendanceServiceRow(
    ctx: StateContext<AttendanceServicesStateModel>,
    {
      serviceConceptId,
      dayIndex,
      amount,
    }: AttendanceServicesActions.EditAttendanceServiceRow
  ) {
    var state = ctx.getState();

    let invoicingServicesItem = state.invoicingServicesItems.find(
      (x) => x.serviceConceptId === serviceConceptId
    );
    let serviceDayItem = invoicingServicesItem.serviceDays[dayIndex];
    let isEdited = serviceDayItem.amount != amount;

    ctx.setState(
      patch({
        invoicingServicesItemsResponse:
          updateItem<InvoicingServiceItemResponse>(
            (sc) => sc.serviceConceptId === serviceConceptId,
            patch({
              serviceDays: updateItem<any>(
                (d) => d.day === dayIndex + 1,
                patch({
                  amount: amount,
                  updated: isEdited,
                })
              ),
              edited: isEdited,
            })
          ),
      })
    );
  }

  @Action(AttendanceServicesActions.SaveAttendancesServices)
  saveAttendancesServices(
    ctx: StateContext<AttendanceServicesStateModel>,
    {
      invoicingServicesForm,
      user,
    }: AttendanceServicesActions.SaveAttendancesServices
  ) {
    this.alertService
      .confirm(
        this.getTitle(),
        this.localizationService.translate('invoicingServicesDayList.title'),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'invoicingServicesDayList.invoicingServicesSaveConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          var state = ctx.getState();
          ctx.patchState({
            isLoading: true,
          });

          let attendancesServicesResponse = this.getAttendanceServicesResponse(
            invoicingServicesForm,
            state.invoicingServicesItemsResponse
          );
          return this.attendanceServicesService
            .saveAttendancesServices(user, attendancesServicesResponse)
            .subscribe({
              next: () =>
                ctx.dispatch(
                  new AttendanceServicesActions.SaveAttendancesServicesSuccess()
                ),
              error: (error) =>
                ctx.dispatch(new AttendanceServicesActions.Error(error)),
            });
        } else {
          return null;
        }
      });
  }

  @Action(AttendanceServicesActions.SaveAttendancesServicesSuccess)
  saveAttendancesServicesSuccess(
    ctx: StateContext<AttendanceServicesStateModel>,
    {}: AttendanceServicesActions.SaveAttendancesServicesSuccess
  ) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoading: false,
    });
  }

  @Action(AttendanceServicesActions.Error)
  onErrorOcurred(
    ctx: StateContext<AttendanceServicesStateModel>,
    { error }: AttendanceServicesActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(
      this.getTitle(),
      error,
      this.alertService,
      this.localizationService
    );
  }

  @Action(AttendanceServicesActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<AttendanceServicesStateModel>,
    { isListSmaller }: AttendanceServicesActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(AttendanceServicesActions.SetAttendanceServicesDefaultState)
  setAttendanceServicesDefaultState(
    ctx: StateContext<AttendanceServicesStateModel>,
    {}: AttendanceServicesActions.SetAttendanceServicesDefaultState
  ) {
    ctx.setState(
      patch({
        invoicingServiceForm: patch({
          model: defaults.invoicingServiceForm.model,
        }),
      })
    );
  }

  private getAttendanceServicesItemsResponse(
    invoicingServicesForm: InvoicingServicesForm
  ) {
    let servicesConceptsArray: InvoicingServiceItemResponse[] = [];

    invoicingServicesForm.servicesConcepts.forEach((element) => {
      let item = new InvoicingServiceItemResponse();
      item.serviceConceptId = element.serviceConceptId;
      item.serviceDays = element.serviceDays;
      servicesConceptsArray.push(item);
    });

    return servicesConceptsArray;
  }

  private getAttendanceServicesResponse(
    invoicingServicesForm: InvoicingServicesForm,
    items: InvoicingServiceItemResponse[]
  ) {
    let invoicingServicesResponse = new InvoicingServicesResponse();

    invoicingServicesResponse.exerciseId = invoicingServicesForm.exerciseId;
    invoicingServicesResponse.schoolId = invoicingServicesForm.schoolId;
    invoicingServicesResponse.year = invoicingServicesForm.year;
    invoicingServicesResponse.month = invoicingServicesForm.month;

    let servicesConceptsArray: InvoicingServiceItemResponse[] = [];
    items.forEach((element) => {
      if (element.edited) {
        let item = new InvoicingServiceItemResponse();
        item.serviceConceptId = element.serviceConceptId;

        let serviceDaysArray: ServiceDayData[] = [];
        element.serviceDays.forEach((day) => {
          if (day.updated) {
            let dayItem = new ServiceDayData();
            dayItem.day = day.day;
            dayItem.amount = day.amount;
            serviceDaysArray.push(dayItem);
          }
        });

        item.serviceDays = serviceDaysArray;
        servicesConceptsArray.push(item);
      }
    });
    invoicingServicesResponse.servicesConcepts = servicesConceptsArray;

    return invoicingServicesResponse;
  }

  private getTitle() {
    return this.localizationService.translate(
      'invoicingServicesDayFilter.title'
    );
  }
}
